/* eslint-disable import/prefer-default-export */
/* eslint-disable no-shadow */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import {
	createWeb3Modal,
	defaultConfig,
	useDisconnect,
	useWeb3ModalAccount,
	useWeb3ModalProvider,
} from '@web3modal/ethers5/react';
import { ethers } from 'ethers';
import Web3 from 'web3';

import { ChainType, MENU_NETWORK_OPTION } from 'src/constants/network';
import useAuthReducer from 'src/hooks/reducers/useAuthReducer';
import { IXellarCurrentChain } from 'src/interface/Xellar';
import { resetWeb3State, setWeb3Balance } from 'src/redux/slices/web3Slice';
import { selectWeb3 } from 'src/redux/store';
import { notify } from 'src/utils/notify';
import web3config from 'src/utils/web3config';

export interface IWeb3modalChain {
	chainId: number;
	name: string;
	currency: string;
	explorerUrl: string;
	rpcUrl: string;
	icon?: string;
}

// 1. Get projectId at https://cloud.walletconnect.com
const { projectId } = web3config;

// 2. Set chains
const mainnet: IWeb3modalChain = {
	chainId: 1,
	name: 'Ethereum',
	currency: 'ETH',
	explorerUrl: 'https://etherscan.io',
	rpcUrl: 'https://cloudflare-eth.com',
	icon: 'https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg',
};

const bsc: IWeb3modalChain = {
	chainId: 56,
	name: 'BNB Smart Chain',
	currency: 'BNB',
	explorerUrl: 'https://bscscan.com',
	rpcUrl: 'https://rpc.ankr.com/bsc',
	icon: 'https://icons.llamao.fi/icons/chains/rsz_binance.jpg',
};

const arbitrum: IWeb3modalChain = {
	chainId: 42161,
	name: 'Arbitrum One',
	currency: 'ETH',
	explorerUrl: 'https://arbiscan.io',
	rpcUrl: 'https://arb1.arbitrum.io/rpc',
	icon: 'https://icons.llamao.fi/icons/chains/rsz_arbitrum.jpg',
};

const okc: IWeb3modalChain = {
	chainId: 66,
	name: 'OKC',
	currency: 'OKT',
	explorerUrl: 'https://www.oklink.com/okc',
	rpcUrl: 'https://exchainrpc.okex.org',
	icon: 'https://icons.llamao.fi/icons/chains/rsz_okexchain.jpg',
};

export const web3modalChains: IWeb3modalChain[] = [mainnet, bsc, arbitrum, okc];

// 3. Create modal
const metadata = {
	name: 'My Website',
	description: 'My Website description',
	url: 'https://creoplay.app',
	icons: ['https://creoplay.app/static/favicon-96x96.png'],
};

createWeb3Modal({
	ethersConfig: defaultConfig({
		metadata,
		enableInjected: false,
		enableCoinbase: true,
		rpcUrl: 'https://cloudflare-eth.com', // used for the Coinbase SDK
		defaultChainId: 1, // used for the Coinbase SDK
	}),
	chains: web3modalChains,
	defaultChain: web3modalChains[0],
	projectId,
	featuredWalletIds: [
		'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96',
		// 'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa',
		'4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0',
		'38f5d18bd8522c244bdd70cb4a68e0e718865155811c043f052fb9f1c51de662',
		'971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709',
	],
	includeWalletIds: [
		'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96',
		'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa',
		'4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0',
		'38f5d18bd8522c244bdd70cb4a68e0e718865155811c043f052fb9f1c51de662',
		'971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709',
	],
	themeVariables: {
		'--w3m-z-index': 10000,
	},
});

export interface IWeb3State {
	canAccessWeb3: boolean;
	currentChainId: number;
	isXellar: boolean;
	xellarProvider: any;
	xellarCurrentChain: IXellarCurrentChain;
	xellarChainId: number;
	getXellarContract: (contractAbi: string, contractAddress: string, xellarProvider?: any) => any;
	isConnected: boolean;
	web3Address: string;
	provider: any;
	address: string;
	chainId: number;
	creoBalance: any;
	tokenBalance: any;
	connect: () => void;
	disconnect: () => void;
	getContract: (contractAbi: string, contractAddress: string) => any;
	requestWalletSignature: () => any;
}

export const useWeb3 = () => {
	const router = useRouter();

	const { creoAbi, creoAddress, bridgeAbiAndAddress } = web3config;

	const { creoBalance, tokenBalance } = useSelector(selectWeb3);

	const { isConnected, address: web3Address, chainId } = useWeb3ModalAccount();

	const dispatch = useDispatch();

	const { user, clearUser } = useAuthReducer();

	const address = user?.wallet_id ?? web3Address;

	const { disconnect: disconnectWeb3Modal } = useDisconnect();

	const web3ModalProviderData = useWeb3ModalProvider();
	const provider = web3ModalProviderData?.walletProvider as any;
	// const [provider, setProvider] = useState<any>(null);

	const getContract = async (abiString: string, contractAddress: string) => {
		const web3 = new Web3(provider as any);
		const abi = JSON.parse(abiString);
		const contract = new web3.eth.Contract(abi, contractAddress);
		return contract;
	};

	/* Xellar - Start */
	const [xellarProvider, setXellarProvider] = useState<any>(null);
	const xellarCurrentChain = user?.profile?.xellar_current_chain;
	const xellarChainId = xellarCurrentChain?.id ? parseInt(xellarCurrentChain?.id) : undefined;
	const isLoggedInXellar = user?.profile?.wallet_type === 'xellar';

	const getXellarContract = async (
		contractAbi: string,
		contractAddress: string,
		pXellarProvider?: any,
	) => {
		const contract = new ethers.Contract(
			contractAddress,
			contractAbi,
			pXellarProvider ?? xellarProvider,
		);
		return contract;
	};

	const getBalances = async () => {
		const currentChain = Number(xellarCurrentChain?.id || 0);
		const chainName: ChainType =
			(MENU_NETWORK_OPTION.find((item) => item.chain_id === currentChain)?.id as ChainType) ||
			'bsc_mainnet';

		const config = {
			abi: JSON.stringify(bridgeAbiAndAddress?.[chainName].token.abi || creoAbi),
			address: bridgeAbiAndAddress?.[chainName].token.address || creoAddress,
		};

		if (isLoggedInXellar) {
			if (!xellarProvider) {
				return;
			}

			if (config.abi && config.address) {
				try {
					const creoContract = await getXellarContract(config.abi, config.address);
					if (creoContract) {
						const nTokenBalance = await creoContract.balanceOf(address);
						const nCreoBalance = Web3.utils.fromWei(nTokenBalance.toString());

						dispatch(
							setWeb3Balance({
								creoBalance: parseFloat(nCreoBalance),
								tokenBalance: Number(nTokenBalance),
							}),
						);
					}
				} catch (err: any) {
					console.error(err);
				}
			}
		} else {
			if (!provider) {
				return;
			}

			const web3Provider = new ethers.providers.Web3Provider(provider);
			const signer = web3Provider.getSigner();
			const nAddress = await signer.getAddress();

			if (config.abi && config.address) {
				const web3 = new Web3(provider as any);
				const creoContract = await getContract(config.abi, config.address);
				if (creoContract) {
					const nTokenBalance = await creoContract.methods.balanceOf(nAddress).call();
					const nCreoBalance = web3.utils.fromWei(nTokenBalance);

					dispatch(
						setWeb3Balance({
							creoBalance: parseFloat(nCreoBalance),
							tokenBalance: Number(nTokenBalance),
						}),
					);
				}
			}
		}
	};

	const requestWalletSignature = async () => {
		const nonce = Math.floor(Math.random() * 1000000); // Random nonce for the message
		const message = `x${nonce}: Please sign this message to confirm wallet ownership`;

		try {
			const web3Provider = new ethers.providers.Web3Provider(provider);
			const signer = web3Provider.getSigner();

			const signature = await signer.signMessage(message);
			console.log('Signature received:', signature);
			// You can verify the signature on the server if needed

			return true;
		} catch (error: any) {
			console.error('User rejected signature request:', error);
			notify(error?.message ?? '', 'error');
			return false;
		}
	};

	useEffect(() => {
		if (isLoggedInXellar) {
			if (xellarCurrentChain?.rpc_url) {
				const newXellarProvider = new ethers.providers.JsonRpcProvider(xellarCurrentChain.rpc_url);
				if (newXellarProvider) {
					// getXellarChainId(newXellarProvider);
					setXellarProvider(newXellarProvider);
				}
			} else {
				setXellarProvider(null);
			}
		} else if (!user || (user?.profile && !isLoggedInXellar)) {
			setXellarProvider(null);
		}
	}, [JSON.stringify(user?.profile)]);

	useEffect(() => {
		getBalances();
	}, [xellarProvider, provider, router.pathname]);
	/* Xellar - End */

	const connect = async () => {
		if (provider) {
			try {
				getBalances();
			} catch (e) {
				// console.log('connect error', e);
			}
		} else {
			// console.error('No Web3Modal');
			localStorage.removeItem('WEB3_CONNECT_CACHED_PROVIDER');
		}
	};

	const disconnect = async () => {
		if (provider) {
			// if (user?.wallet_id === web3Address) {
			// clearUser();
			// }

			if (disconnectWeb3Modal && typeof disconnectWeb3Modal === 'function') {
				// console.log({ disconnectWeb3Modal });
				await disconnectWeb3Modal?.();
			}

			dispatch(resetWeb3State());
		} else {
			// console.error('No Web3Modal');
		}
	};

	// Auto connect to the cached provider
	useEffect(() => {
		if (provider) {
			connect();
		}
	}, [provider]);

	// EIP-1193 events
	// eslint-disable-next-line consistent-return
	useEffect(() => {
		if (provider?.on) {
			const handleAccountsChanged = () => {
				clearUser();

				if (typeof window !== 'undefined') {
					// console.log('switched to chain...', _hexChainId);
					window.location.reload();
				} else {
					// console.log('window is undefined');
				}
			};

			// eslint-disable-next-line no-unused-vars
			const handleChainChanged = () =>
				// _hexChainId: string
				{
					if (typeof window !== 'undefined') {
						// console.log('switched to chain...', _hexChainId);
						window.location.reload();
					} else {
						// console.log('window is undefined');
					}
				};

			const handleDisconnect = (/*error: { code: number; message: string }*/) => {
				localStorage.removeItem('WEB3_CONNECT_CACHED_PROVIDER');
				// eslint-disable-next-line no-console
				// console.log('disconnect', error);
				disconnect();
			};

			provider.on('accountsChanged', handleAccountsChanged);
			provider.on('chainChanged', handleChainChanged);
			provider.on('disconnect', handleDisconnect);

			// Subscription Cleanup
			return () => {
				if (provider.removeListener) {
					provider.removeListener('accountsChanged', handleAccountsChanged);
					provider.removeListener('chainChanged', handleChainChanged);
					provider.removeListener('disconnect', handleDisconnect);
				}
			};
		}
	}, [provider, disconnect]);

	return {
		canAccessWeb3:
			(provider !== undefined && provider !== null) ||
			(user?.profile?.wallet_type === 'xellar' &&
				xellarProvider !== undefined &&
				xellarProvider !== null),
		currentChainId: isLoggedInXellar ? xellarChainId : chainId,
		isXellar: isLoggedInXellar,
		xellarProvider,
		xellarCurrentChain,
		xellarChainId,
		getXellarContract,
		isConnected,
		web3Address,
		provider,
		address,
		chainId,
		creoBalance,
		tokenBalance,
		connect,
		disconnect,
		getContract,
		requestWalletSignature,
	} as IWeb3State;
};
